import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getBranchesForPOS } from 'service/branch.service';
import { getDeliveryAreas, getDeliveryAreasForKitchenAndBrand } from 'service/deliveryArea.service';
import * as orderService from 'service/order.service';
import type store from 'store';
import type { AppDispatch } from 'store';
import type { PunchOrderFlowDiscountsState } from 'store/punchOrderFlowDiscounts/punchOrderFlowDiscounts.types';
import type { SettingsState } from 'store/settings/settings.reducer';
import type { Kitchen } from 'types/Kitchen';
import type { OrderPosition } from 'types/OrderPosition';
import type { OrderPriceDetails } from 'types/OrderPriceDetails';
import type { FeatureFlags } from 'utils/constants';
import { dialog } from 'utils/dialog';
import { setCurrency } from './punchOrderFlow.actions';
import type { PunchOrderFlowState } from './punchOrderFlow.types';
import { getCreateOrderRequestBody } from './utils/getCreateOrderRequestBody.util';
import { getValidateOrderRequestBody } from './utils/getValidateOrderRequestBody.util';

export * from './orderBody/orderBody.actions';
export * from './orderDetails/orderDetails.actions';
export * from './orderSource/orderSource.actions';
export * from './selectedCustomer/selectedCustomer.actions';

interface OrderCreationParams {
  compositionTime: number;
  features?: FeatureFlags;
}

export const punchOrder = createAsyncThunk<
  unknown,
  OrderCreationParams,
  {
    state: {
      punchOrderFlow: PunchOrderFlowState;
      settings: SettingsState;
      punchOrderFlowDiscounts: PunchOrderFlowDiscountsState;
    };
  }
>('punchOrderFlow/punchOrder', (orderParams: OrderCreationParams, { getState }) => {
  const state = getState();
  const requestBody = getCreateOrderRequestBody(state, orderParams.compositionTime);
  if (!requestBody) {
    const message = 'Lack of order body. Please try to create order again.';
    dialog.error({ message, title: '' });
    return Promise.reject(message);
  }
  return orderService.punchNewOrder({
    orderBody: requestBody,
  });
});

export const setInitialState = createAction<PunchOrderFlowState>('punchOrderFlow/setInitialState');

export const fetchBranchesForPOS = createAsyncThunk(
  'punchOrderFlow/fetchBranchesForPOS',
  getBranchesForPOS
);

export const setCustomDeliveryFee = createAction<string>('punchOrderFlow/setCustomDeliveryFee');

export const resetPunchOrderFlow = createAction('punchOrderFlow/resetPunchOrderFlow');

export const setRegionCurrency =
  (regionId: string) => (dispatch: AppDispatch, getState: typeof store.getState) => {
    const region = getState().metadata.regions.find(region => region.id === regionId);
    const country = getState().metadata.countries.find(country => country.id === region?.countryId);
    dispatch(setCurrency(country?.currency || null));
  };

export const resolveKitchen = createAction<Kitchen | 'KITCHEN_NOT_AVAILABLE' | null>(
  'punchOrderFlow/resolveKitchen'
);

export const setDefaultKitchenForBrandAndDeliveryArea = createAction(
  'punchOrderFlow/setDefaultKitchenForBrandAndDeliveryArea'
);

export const getDeliveryAreasForBranch = createAsyncThunk(
  'punchOrderFlow/getDeliveryAreasForBranch',
  getDeliveryAreasForKitchenAndBrand
);

export const getDeliveryAreasForRegion = createAsyncThunk(
  'punchOrderFlow/getDeliveryAreasForRegion',
  getDeliveryAreas
);

export const applyDiscounts = createAsyncThunk<
  {
    orderPriceDetails: OrderPriceDetails;
    orderPositions: OrderPosition[] | undefined;
  },
  undefined,
  {
    state: {
      punchOrderFlow: PunchOrderFlowState;
      punchOrderFlowDiscounts: PunchOrderFlowDiscountsState;
      settings: SettingsState;
    };
  }
>('punchOrderFlow/applyDiscounts', (_, { getState }) => {
  const { punchOrderFlow, punchOrderFlowDiscounts, settings } = getState();

  const pointOfSaleId = punchOrderFlow.orderSource.selectedPos?.pointOfSaleId;
  const requestBody = getValidateOrderRequestBody({
    punchOrderFlow,
    punchOrderFlowDiscounts,
    settings,
  });

  if (!requestBody) {
    return Promise.reject();
  }

  return orderService
    .validateOrder({
      posID: pointOfSaleId,
      orderBody: requestBody,
    })
    .then(({ orderPriceDetails, orderPositions }) => ({
      orderPriceDetails: {
        ...orderPriceDetails,
        taxAmount: orderPriceDetails?.taxes?.[0]?.taxValue ?? 0,
      },
      orderPositions,
    }));
});
